import React from 'react';
import {Link} from 'react-router-dom';

import {CriteriaStrip, Stripe} from 'src/components/criteria-strip/criteria-strip';
import {ColumnHeader} from 'src/components/filter-header/column-headers/column-header';
import {DateColumnHeader} from 'src/components/filter-header/column-headers/date-column-header';
import {DateRange} from 'src/components/filter-header/typedefs';
import {NoResultsRow} from 'src/components/no-results-row/no-results-row';
import {SortConfig} from 'src/hooks/useSortingRows';
import {createEventRoutePath} from 'src/modules/event/event-route-planner';
import {createPredicates} from 'src/modules/events/filter-utils/createPredicates';
import {extractColumnHeaderData} from 'src/modules/events/filter-utils/extractColumnHeaderData';
import {Filters, SortColumns} from 'src/modules/events/typedefs';
import {EventBasicInfo} from 'src/openapi/models/EventBasicInfo';
import {copy} from 'src/utils/copyToClipboard';

import {Dropdown} from '@pb/kit-component-dropdown-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import Group, {Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton, InfoIconButton} from '@pb/kit-component-icon-button';
import Loading from '@pb/kit-component-loading';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {formatDate} from '@pb/kit-formatters';


import {COLUMN_CAPTIONS, ColumnTypes, ColumnWidth} from '../constants';

import {AttributesPillsStrip} from './attributes-pills-strip/attributes-pills-strip';

import {eventsDeprecated} from './events.module.styl';

type Props = {
    preparedRows: Array<EventBasicInfo>,
    rows: Array<EventBasicInfo>,
    setFilter: (filterType: string, filterValue: Array<string> | DateRange) => void,
    filters: Filters,
    onSort: (config: SortConfig<SortColumns>) => void,
    sortConfig: SortConfig<SortColumns>,
    criteriaStripeData: Array<Stripe>,
    clearAllFilters: () => void,
    intersectRef: (node: Element | null) => void,
    isVisible: boolean,
    hasMore: boolean,
};

const AMOUNT_LEFT_TO_UPLOAD_NEXT = 5;

export const EventsView = (props: Props) => {
    const {
        preparedRows,
        rows,
        setFilter,
        filters,
        onSort,
        sortConfig,
        criteriaStripeData,
        clearAllFilters,
        intersectRef,
        isVisible,
        hasMore,
    } = props;

    return (
        <Group direction="v" gap="xxs">
            <CriteriaStrip data={criteriaStripeData} clearAllFilters={clearAllFilters}/>
            <Table>
                <Head>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_TYPE]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_TYPE}
                            sortingColumnType={ColumnTypes.EVENT_TYPE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_TYPE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_SOURCE]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_SOURCE}
                            sortingColumnType={ColumnTypes.EVENT_SOURCE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_SOURCE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_FAMILIES]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            filteringColumnType={ColumnTypes.EVENT_FAMILIES}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_FAMILIES]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_CODE]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_CODE}
                            sortingColumnType={ColumnTypes.EVENT_CODE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_CODE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_DESCRIPTION]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_DESCRIPTION}
                            sortingColumnType={ColumnTypes.EVENT_DESCRIPTION}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_DESCRIPTION]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_ATTRIBUTES]}>
                        <ColumnHeader
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            rows={rows}
                            filteringColumnType={ColumnTypes.EVENT_ATTRIBUTES}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_ATTRIBUTES]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_FIRST_USAGE_DATE]}>
                        <DateColumnHeader
                            setFilter={setFilter}
                            filters={filters}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_FIRST_USAGE_DATE}
                            sortingColumnType={ColumnTypes.EVENT_FIRST_USAGE_DATE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_FIRST_USAGE_DATE]}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_LAST_USAGE_DATE]}>
                        <DateColumnHeader
                            setFilter={setFilter}
                            filters={filters}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_LAST_USAGE_DATE}
                            sortingColumnType={ColumnTypes.EVENT_LAST_USAGE_DATE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_LAST_USAGE_DATE]}
                        />
                    </Cell>
                </Head>
                <Body>
                    {
                        preparedRows.length ? (
                            preparedRows.map((row, i, arr) => (
                                <Row
                                    className={row.deprecated ? eventsDeprecated : ''}
                                    key={row.id}
                                    ref={node => (i === arr.length - AMOUNT_LEFT_TO_UPLOAD_NEXT
                                        ? intersectRef(node as Element) : undefined)}>
                                    <Cell><EllipsisTextHint>{row[ColumnTypes.EVENT_TYPE]}</EllipsisTextHint></Cell>
                                    <Cell><EllipsisTextHint>{row[ColumnTypes.EVENT_SOURCE]}</EllipsisTextHint></Cell>
                                    <Cell>
                                        <Group direction="v" gap="xs">
                                            {row[ColumnTypes.EVENT_FAMILIES].map(family => (
                                                <Item key={family}>
                                                    <EllipsisTextHint>
                                                        {family}
                                                    </EllipsisTextHint>
                                                </Item>
                                            ))}
                                        </Group>
                                    </Cell>
                                    <Cell>
                                        {row.deprecated ? (
                                            <Group justify="space-between" alignItems="center" gap="xs">
                                                <EllipsisTextHint>
                                                    <Link
                                                        className={eventsDeprecated}
                                                        to={createEventRoutePath({eventId: row.id})}>
                                                        {row[ColumnTypes.EVENT_CODE]}
                                                    </Link>
                                                </EllipsisTextHint>
                                                <Item>
                                                    <Dropdown
                                                        hoverEnabled={true}
                                                        clickEnabled={false}
                                                        maxWidth={500}
                                                        anchor={({anchorRef, getAnchorProps}) => (
                                                            <InfoIconButton {...getAnchorProps()} ref={anchorRef}/>
                                                        )}>
                                                        Event is deprecated
                                                    </Dropdown>
                                                </Item>
                                            </Group>
                                        ) : (
                                            <Link to={createEventRoutePath({eventId: row.id})}>
                                                <EllipsisTextHint>{row[ColumnTypes.EVENT_CODE]}</EllipsisTextHint>
                                            </Link>
                                        )}
                                    </Cell>
                                    <Cell>
                                        <Group justify="space-between" alignItems="center">
                                            <EllipsisTextHint>{row[ColumnTypes.EVENT_DESCRIPTION]}</EllipsisTextHint>
                                            <BasicIconButton
                                                iconProps={{type: Icons.COPY}}
                                                onClick={() => copy(row[ColumnTypes.EVENT_DESCRIPTION])}
                                            />
                                        </Group>
                                    </Cell>
                                    <Cell>
                                        <AttributesPillsStrip attributesData={row[ColumnTypes.EVENT_ATTRIBUTES]}/>
                                    </Cell>
                                    <Cell>
                                        {row.firstUsageDate ? formatDate(row.firstUsageDate) : ''}
                                    </Cell>
                                    <Cell>
                                        {row.lastUsageDate ? formatDate(row.lastUsageDate) : ''}
                                    </Cell>
                                </Row>
                            ))
                        ) : <NoResultsRow colSpan={7}/>
                    }
                </Body>
            </Table>
            {(isVisible && hasMore) ? <Loading fast={true}/> : null}
        </Group>
    );
};
